import React, { useState, useMemo } from 'react';
import { leaveLedgerApi } from '../services/api';
import { LeaveLedgerReportEntry } from '../types';
import { Calendar, Download, FileSpreadsheet, Loader2, RefreshCw } from 'lucide-react';
import * as XLSX from 'xlsx';
import { formatCompanyDate, formatCompanyDateTime } from '../utils/companyDateFormat';

const LeaveLedgerReport: React.FC = () => {
  const defaultEnd = new Date();
  const defaultStart = new Date();
  defaultStart.setDate(defaultStart.getDate() - 30);
  const [startDate, setStartDate] = useState<string>(defaultStart.toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState<string>(defaultEnd.toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entries, setEntries] = useState<LeaveLedgerReportEntry[]>([]);

  const loadReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await leaveLedgerApi.getReport(startDate, endDate);
      setEntries(data || []);
    } catch (e: any) {
      setError(e.response?.data?.message || e.message || 'Failed to load leave ledger report.');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const headers = [
    'Employee',
    'Date',
    'Source Type',
    'Event Type',
    'Days',
    'Hours',
    'Payroll Reference',
    'Notes',
    'Created At',
  ];

  const rows: string[][] = useMemo(() => {
    return entries.map((e) => [
      e.employeeName || `Employee ${e.employeeId}`,
      formatCompanyDate(e.date) || e.date,
      e.sourceType,
      e.eventType,
      String(e.days ?? ''),
      e.hours != null ? String(e.hours) : '',
      e.payrollReference ?? '',
      e.notes ?? '',
      e.createdAt ? formatCompanyDateTime(e.createdAt) : '',
    ]);
  }, [entries]);

  const downloadCsv = () => {
    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Leave_Ledger_Report_${startDate}_to_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadExcel = () => {
    const wsData = [headers, ...rows];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, 'Leave Ledger');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Leave_Ledger_Report_${startDate}_to_${endDate}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-neutral-900">Leave Ledger Report</h2>
        <p className="text-sm text-neutral-600 mt-1">
          Verify leave ledger data (annual leave, public holidays, etc.) for the selected date range.
        </p>
      </div>

      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border border-neutral-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border border-neutral-300 rounded-lg"
            />
          </div>
          <button
            onClick={loadReport}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Load Report
          </button>
          {entries.length > 0 && (
            <>
              <button
                onClick={downloadCsv}
                className="flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50"
              >
                <Download className="h-4 w-4" />
                Export CSV
              </button>
              <button
                onClick={downloadExcel}
                className="flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50"
              >
                <FileSpreadsheet className="h-4 w-4" />
                Export Excel
              </button>
            </>
          )}
        </div>
        {error && (
          <p className="mt-3 text-sm text-red-600">{error}</p>
        )}
      </div>

      <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-neutral-200 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-neutral-500" />
          <span className="font-medium text-neutral-800">
            Leave Ledger Entries ({entries.length})
          </span>
        </div>
        <div className="overflow-x-auto">
          {entries.length === 0 && !loading && (
            <p className="p-6 text-neutral-500 text-center">
              Select a date range and click Load Report to view data.
            </p>
          )}
          {entries.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-neutral-50 border-b border-neutral-200">
                  {headers.map((h) => (
                    <th key={h} className="px-4 py-3 text-left font-medium text-neutral-700 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                    <td className="px-4 py-2">{e.employeeName || `Employee ${e.employeeId}`}</td>
                    <td className="px-4 py-2">{formatCompanyDate(e.date) || e.date}</td>
                    <td className="px-4 py-2">{e.sourceType}</td>
                    <td className="px-4 py-2">{e.eventType}</td>
                    <td className="px-4 py-2">{e.days}</td>
                    <td className="px-4 py-2">{e.hours ?? '—'}</td>
                    <td className="px-4 py-2">{e.payrollReference ?? '—'}</td>
                    <td className="px-4 py-2 max-w-xs truncate" title={e.notes ?? ''}>{e.notes ?? '—'}</td>
                    <td className="px-4 py-2 text-neutral-500">
                      {e.createdAt ? formatCompanyDateTime(e.createdAt) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaveLedgerReport;
